/**
 * Delivery cities — the list of cities a customer picks from at storefront
 * checkout, each with its own delivery fee (store currency; 0 = free). Lives
 * under the Delivery section but the source of truth is cms_settings; we PATCH
 * only the deliveryCities field (partial update, leaves other CMS settings alone).
 */
import type { CmsSettings, DeliveryCity } from '@erp/shared';
import { MapPin, Plus, Save, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, EmptyState, Input } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

const PRICE_RE = /^\d+(\.\d{1,4})?$/;

export function DeliveryCitiesPage(): JSX.Element {
  const { t } = useTranslation('delivery');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('cms.write');

  const [settings, setSettings] = useState<CmsSettings | null>(null);
  const [cities, setCities] = useState<DeliveryCity[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [feeInput, setFeeInput] = useState('');

  useEffect(() => {
    void api()
      .get<CmsSettings>('/cms/settings')
      .then((r) => { setSettings(r.data); setCities(r.data.deliveryCities ?? []); })
      .catch(() => undefined);
  }, []);

  const currency = settings?.currencyCode ?? settings?.baseCurrencyCode ?? '';

  const addCity = (): void => {
    const name = nameInput.trim();
    if (!name) return;
    if (cities.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      toast.error(t('cities.duplicate', { defaultValue: 'That city is already in the list.' }));
      return;
    }
    const raw = feeInput.trim();
    const price = PRICE_RE.test(raw) ? raw : '0';
    setCities((prev) => [...prev, { name, price }]);
    setNameInput('');
    setFeeInput('');
    setDirty(true);
  };
  // Rows are edited by index (both name and fee are editable in place) so a city
  // can be renamed — the name is no longer its identity while editing.
  const removeCity = (idx: number): void => {
    setCities((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };
  const setName = (idx: number, name: string): void => {
    setCities((prev) => prev.map((c, i) => (i === idx ? { ...c, name } : c)));
    setDirty(true);
  };
  const setFee = (idx: number, price: string): void => {
    setCities((prev) => prev.map((c, i) => (i === idx ? { ...c, price } : c)));
    setDirty(true);
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      // Partial patch — only touch deliveryCities, leave every other CMS setting.
      // Drop rows whose name was cleared (the schema requires a non-empty name).
      const payload = {
        deliveryCities: cities
          .filter((c) => c.name.trim())
          .map((c) => ({
            name: c.name.trim(),
            price: PRICE_RE.test(String(c.price ?? '').trim()) ? String(c.price).trim() : '0',
          })),
      };
      const r = await api().put<CmsSettings>('/cms/settings', payload);
      setSettings(r.data);
      setCities(r.data.deliveryCities ?? []);
      setDirty(false);
      toast.success(t('cities.saved', { defaultValue: 'Delivery cities saved.' }));
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <Card className="p-6"><Skeleton className="h-64 w-full" /></Card>;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('cities.title', { defaultValue: 'Delivery cities' })}</h1>
          <p className="mt-1 max-w-2xl text-sm text-fg-muted">
            {t('cities.description', { defaultValue: 'Cities customers pick from at checkout, each with its own delivery fee (0 = free). Leave the list empty to let customers type any city.' })}
          </p>
        </div>
        {canWrite && (
          <Button onClick={save} disabled={!dirty || saving}>
            <Save className="me-1.5 h-4 w-4" aria-hidden />
            {saving ? tc('actions.saving', { defaultValue: 'Saving…' }) : tc('actions.save', { defaultValue: 'Save' })}
          </Button>
        )}
      </header>

      {canWrite && (
        <Card className="flex flex-wrap items-end gap-2 p-4">
          <label className="flex flex-1 flex-col gap-1 text-sm" style={{ minWidth: '12rem' }}>
            <span className="text-fg-muted">{t('cities.cityName', { defaultValue: 'City name' })}</span>
            <Input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCity(); } }}
              maxLength={80}
              placeholder={t('cities.cityNamePlaceholder', { defaultValue: 'e.g. Duhok' })}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-fg-muted">{t('cities.fee', { defaultValue: 'Delivery fee' })}</span>
            <div className="flex items-center gap-1.5">
              <Input
                value={feeInput}
                onChange={(e) => setFeeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCity(); } }}
                inputMode="decimal"
                placeholder="0"
                className="w-32"
              />
              <span className="text-xs text-fg-muted">{currency}</span>
            </div>
          </label>
          <Button type="button" variant="secondary" disabled={!nameInput.trim()} onClick={addCity}>
            <Plus className="me-1 h-4 w-4" aria-hidden />
            {t('cities.add', { defaultValue: 'Add city' })}
          </Button>
        </Card>
      )}

      {cities.length === 0 ? (
        <EmptyState
          icon={<MapPin className="h-8 w-8" aria-hidden />}
          title={t('cities.empty', { defaultValue: 'No delivery cities yet' })}
          description={t('cities.emptyDescription', { defaultValue: 'Add the cities you deliver to and their fees. With none set, customers can type any city at checkout.' })}
        />
      ) : (
        <Card className="flex flex-col gap-2 p-4">
          <div className="flex items-center justify-between px-1 text-xs font-medium uppercase tracking-wide text-fg-muted">
            <span>{t('cities.cityName', { defaultValue: 'City name' })}</span>
            <span>{t('cities.fee', { defaultValue: 'Delivery fee' })}</span>
          </div>
          {cities.map((c, idx) => (
            <div key={idx} className="flex items-center gap-2 rounded-md border border-border bg-bg-subtle px-3 py-2 text-sm sm:gap-3">
              <MapPin className="h-4 w-4 flex-shrink-0 text-fg-muted" aria-hidden />
              <Input
                aria-label={t('cities.cityName', { defaultValue: 'City name' })}
                value={c.name}
                onChange={(e) => setName(idx, e.target.value)}
                disabled={!canWrite}
                placeholder={t('cities.cityNamePlaceholder', { defaultValue: 'e.g. Duhok' })}
                className="h-8 min-w-0 flex-1 font-medium"
              />
              <div className="flex flex-shrink-0 items-center gap-1.5">
                <Input
                  aria-label={`${t('cities.fee', { defaultValue: 'Delivery fee' })} — ${c.name}`}
                  value={c.price}
                  onChange={(e) => setFee(idx, e.target.value)}
                  disabled={!canWrite}
                  inputMode="decimal"
                  className="h-8 w-24"
                />
                <span className="w-9 text-xs text-fg-muted">{currency}</span>
              </div>
              {canWrite && (
                <button
                  type="button"
                  onClick={() => removeCity(idx)}
                  className="flex-shrink-0 text-fg-muted transition hover:text-danger"
                  aria-label={`${tc('actions.delete', { defaultValue: 'Remove' })} — ${c.name}`}
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
